// src/pages/DebtStudio/lib/structure.js

import dayjs from "lib/dayjs";
import { solveLDSStructure } from "pages/DebtStudio/components/Issues/DealStructure/hooks/ldsSolver";
import { solveLevelPrincipalStructure } from "pages/DebtStudio/components/Issues/DealStructure/hooks/levelPrincipalSolver";

/* ────────────────────────────────────────────────
   Frequency Helper
   Converts human-readable frequency strings to
   periods per year (e.g., "semiannual" → 2)
──────────────────────────────────────────────── */
function freqToPeriods(freq) {
  const f = (freq || "").toLowerCase();
  if (f.startsWith("month")) return 12;
  if (f.startsWith("quarter")) return 4;
  if (f.startsWith("semi")) return 2;
  if (f.startsWith("annual")) return 1;
  return 1;
}

function normalizeFinancingType(value) {
  return String(value || "bond").trim().toLowerCase();
}

/* ────────────────────────────────────────────────
   Build a preliminary structure
   - lays out maturity rows only (no par allocation)
   - used when building from scratch
──────────────────────────────────────────────── */
export function buildStructure(params = {}) {
  const {
    couponRate,
    firstPrincipalDate,
    finalMaturityDate,
    principalFrequency,
    pricingMode,
    scale,
  } = params;

  const firstPrin = firstPrincipalDate ? dayjs(firstPrincipalDate) : null;
  const maturity = finalMaturityDate ? dayjs(finalMaturityDate) : null;

  if (!firstPrin?.isValid() || !maturity?.isValid()) return { rows: [] };

  const periodsPerYear = freqToPeriods(principalFrequency);
  const stepMonths = Math.round(12 / periodsPerYear);

  const rows = [];
  const appendRow = (date) => {
    const rowDate = date.format("YYYY-MM-DD");

    let coupon = couponRate ?? null;
    let yld = couponRate ?? null;
    let price = 100.0;

    if (pricingMode === "scale" && Array.isArray(scale)) {
      const pt = scale.find((s) => s.date === rowDate);
      if (pt) {
        coupon = pt.coupon ?? coupon;
        yld = pt.yield ?? yld;
        price = pt.price ?? price;
      }
    }

    rows.push({
      date: rowDate,
      principal: 0,
      coupon,
      yield: yld,
      price,
      bond_type: "serial",
      term_group: null,
      call_date: null,
      call_price: 100,
    });
  };

  let cursor = firstPrin.clone();

  while (cursor.isBefore(maturity) || cursor.isSame(maturity)) {
    appendRow(cursor);

    cursor = cursor.add(stepMonths, "month");
  }

  const maturityISO = maturity.format("YYYY-MM-DD");
  if (rows.length > 0 && rows[rows.length - 1].date !== maturityISO) {
    appendRow(maturity);
  }

  return { rows };
}

/* ────────────────────────────────────────────────
   Top-Level Solver Dispatcher
   - Routes to the correct solver mode
──────────────────────────────────────────────── */
export function solveStructure(params = {}) {
  const {
    structureArray = [],
    structureMode,
    targetType,
    targetValue,

    financingType = "bond",

    amortizationPeriods,
    loanTermPeriods,

    costOfIssuance,
    dayCount,
    couponFrequency,
    principalFrequency,
    datedDate,
    deliveryDate,
    firstCouponDate,
    firstPrincipalDate,
    finalMaturityDate,
    denomination,

    // CapI
    dealType,
    capiEnabled,
    capiDate,
    capiInvestmentRate,

    // Additional Sources
    additionalSources,

    // Optional base DS layer for Wrap-LDS
    baseDebtServiceByPeriod = [],

    // Backward-compatible alias during migration
    wrapBaseByPeriod = [],
  } = params;

  if (!Array.isArray(structureArray) || structureArray.length === 0) {
    return { schedule: [] };
  }

  const normalizedFinancingType = normalizeFinancingType(financingType);

  const commonSolverParams = {
    rows: structureArray,
    structureMode,
    targetType,
    targetValue,

    financingType,
    amortizationPeriods,
    loanTermPeriods,

    costOfIssuance,
    dayCount,
    couponFrequency,
    principalFrequency,
    datedDate,
    deliveryDate,
    firstCouponDate,
    firstPrincipalDate,
    finalMaturityDate,
    denomination,

    dealType,
    capiEnabled,
    capiDate,
    capiInvestmentRate,

    additionalSources,
  };

  switch (structureMode) {
    case "level_payment":
      return solveLDSStructure({
        ...commonSolverParams,
        baseDebtServiceByPeriod: [],
        wrapBaseByPeriod: [],
      });

    case "level_principal": {
      if (normalizedFinancingType !== "loan") {
        throw new Error("Level Principal is currently supported only for loans");
      }

      return solveLevelPrincipalStructure({
        ...commonSolverParams,
        baseDebtServiceByPeriod: [],
        wrapBaseByPeriod: [],
      });
    }

    case "wrap_lds": {
      if (targetType === "periodic_ds") {
        throw new Error("Wrap-LDS does not support targetType='periodic_ds'");
      }

      return solveLDSStructure({
        ...commonSolverParams,
        baseDebtServiceByPeriod: Array.isArray(baseDebtServiceByPeriod)
          ? baseDebtServiceByPeriod
          : [],
        wrapBaseByPeriod: Array.isArray(wrapBaseByPeriod)
          ? wrapBaseByPeriod
          : [],
      });
    }

    default:
      return { schedule: structureArray };
  }
}
